import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GenericModal, Badge, Button, ConfirmDialog } from '@org/ui';
import {
  AnnouncementForm,
  type AnnouncementFormValues,
} from '../../app/components/forms/announcement';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';

export const Route = createFileRoute('/announcements/$announcementId')({
  component: AnnouncementDetailRoute,
});

/** Convert a stored UTC date to a local `datetime-local` input string. */
function toDatetimeLocal(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function AnnouncementDetailRoute() {
  const { announcementId } = Route.useParams();
  const navigate = useNavigate({ from: '/announcements/$announcementId' });
  const utils = trpc.useUtils();
  const { t } = useTranslation('admin');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { data: announcement, isLoading } = trpc.announcements.getById.useQuery({ announcementId });
  const { data: announcements } = trpc.announcements.list.useQuery();
  const stats = announcements?.find((a) => a.announcementId === announcementId);

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.groups.searchActiveGroups.fetch({ search: query });
      return results.map((g) => ({
        value: g.groupId,
        label: g.description ?? g.groupId,
      }));
    },
    [utils],
  );

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.users.searchActiveUsers.fetch({ search: query });
      return results.map((u) => ({
        value: u.userId,
        label: `${u.firstName} ${u.lastName}`,
      }));
    },
    [utils],
  );

  const defaultSelectedGroups = useMemo(
    () =>
      (announcement?.targetGroups ?? []).map((g) => ({
        value: g.groupId,
        label: g.description ?? g.groupId,
      })),
    [announcement],
  );

  const defaultSelectedUsers = useMemo(
    () =>
      (announcement?.targetUsers ?? []).map((u) => ({
        value: u.userId,
        label: `${u.firstName} ${u.lastName}`,
      })),
    [announcement],
  );

  const defaultValues = useMemo(
    () =>
      announcement
        ? {
            title: announcement.title,
            body: announcement.body ?? '',
            mediaType: announcement.mediaType as 'none' | 'image' | 'video' | 'animation',
            mediaUrl: announcement.mediaUrl ?? '',
            posterUrl: announcement.posterUrl ?? '',
            ctaLabel: announcement.ctaLabel ?? '',
            ctaTo: announcement.ctaTo ?? '',
            targetType: announcement.targetType as 'all' | 'e2e' | 'groups' | 'users',
            status: announcement.status as 'draft' | 'active' | 'closed',
            skippable: announcement.skippable,
            startsAt: toDatetimeLocal(announcement.startsAt),
            endsAt: toDatetimeLocal(announcement.endsAt),
            priority: announcement.priority,
            // Announcements created before styles existed have no choice
            // stored; they render (and are shown here) as the classic card.
            style: announcement.style ?? ('classic' as const),
          }
        : undefined,
    [announcement],
  );

  const updateMutation = trpc.announcements.update.useMutation({
    onSuccess: async () => {
      await utils.announcements.list.invalidate();
      await utils.announcements.getById.invalidate({ announcementId });
      toast.success(t('common.toasts.updateSuccess', { entity: 'Announcement' }));
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'update', entity: 'announcement' }));
    },
  });

  const deleteMutation = trpc.announcements.delete.useMutation({
    onSuccess: async () => {
      await utils.announcements.list.invalidate();
      toast.success(t('common.toasts.deleteSuccess', { entity: 'Announcement' }));
      handleClose();
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'delete', entity: 'announcement' }));
    },
  });

  const handleSubmit = (values: AnnouncementFormValues) => {
    updateMutation.mutate({ announcementId, ...values });
  };

  const handleClose = () => {
    navigate({ to: '/announcements', replace: true });
  };

  return (
    <GenericModal
      show
      onClose={handleClose}
      title={announcement?.title ?? t('announcements.details.title')}
      size="xl"
      centered
      body={
        isLoading ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : !announcement ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            {t('announcements.details.notFound')}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary stats */}
            <div className="flex gap-4">
              <Badge variant="outline">
                {stats?.completedCount ?? 0} {t('announcements.details.completed')}
              </Badge>
              <Badge variant="secondary">
                {stats?.impressionCount ?? 0} {t('announcements.details.impressions')}
              </Badge>
            </div>

            <AnnouncementForm
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
              onClose={handleClose}
              isSubmitting={updateMutation.isPending}
              onSearchGroups={searchGroups}
              onSearchUsers={searchUsers}
              defaultSelectedGroups={defaultSelectedGroups}
              defaultSelectedUsers={defaultSelectedUsers}
              includeClosedStatus
              labels={{
                title: t('announcements.form.title'),
                body: t('announcements.form.body'),
                mediaType: t('announcements.form.mediaType'),
                mediaUrl: t('announcements.form.mediaUrl'),
                posterUrl: t('announcements.form.posterUrl'),
                ctaLabel: t('announcements.form.ctaLabel'),
                ctaTo: t('announcements.form.ctaTo'),
                targetType: t('announcements.form.targetType'),
                status: t('announcements.form.status'),
                skippable: t('announcements.form.skippable'),
                startsAt: t('announcements.form.startsAt'),
                endsAt: t('announcements.form.endsAt'),
                priority: t('announcements.form.priority'),
                mediaNone: t('announcements.list.mediaLabels.none'),
                mediaImage: t('announcements.list.mediaLabels.image'),
                mediaVideo: t('announcements.list.mediaLabels.video'),
                mediaAnimation: t('announcements.list.mediaLabels.animation'),
                targetAll: t('announcements.form.targetAll'),
                targetE2e: t('announcements.form.targetE2e'),
                targetGroups: t('announcements.form.targetGroups'),
                targetUsers: t('announcements.form.targetUsers'),
                statusDraft: t('announcements.list.statusLabels.draft'),
                statusActive: t('announcements.list.statusLabels.active'),
                statusClosed: t('announcements.list.statusLabels.closed'),
                skippableOn: t('announcements.form.skippableOn'),
                skippableOff: t('announcements.form.skippableOff'),
                groups: t('announcements.form.groups'),
                users: t('announcements.form.users'),
                datePlaceholder: t('announcements.form.datePlaceholder'),
                style: t('announcements.form.style'),
              }}
            />

            {/* Delete button */}
            <div className="border-t pt-4">
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-sm text-destructive"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleteMutation.isPending}
              >
                {t('announcements.form.delete')}
              </Button>
              <ConfirmDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                description={t('announcements.form.deleteConfirm')}
                confirmLabel={t('announcements.form.delete')}
                destructive
                onConfirm={() => {
                  setConfirmDeleteOpen(false);
                  deleteMutation.mutate({ announcementId });
                }}
              />
            </div>
          </div>
        )
      }
      footer={<></>}
    />
  );
}
